'use client'

import type { ToolFailure } from '@/lib/tool-generations'

// Drives the closed-loop Seedance 2.5 (early-access) tool on /models/seedance-2-5:
//   1. ensureWorkspace() — for a brand-new signup (no org yet), silently create a
//      real self-auth org + incomplete brand so credits exist, then activate it
//      (tokenManager.setActiveOrg + force-refresh) so org-scoped calls resolve.
//      Existing users (>= 1 org) skip this and use their active org.
//   2. generate() — POST /generate; a 402 means "not enough credits" (the page
//      opens the upgrade dialog), otherwise it returns the fal request id.
//   3. pollStatus() — poll the key-backed proxy until the MP4 URL is ready.
//
// NOTE: Seedance 2.5 is not yet on fal (closed beta). The live engine is Seedance
// 2.0 Mini, so pricing / duration / resolution here are IDENTICAL to the mini
// page for now. The backend (apps/api/routers/landing/seedance_2_5.py) is
// authoritative for cost and the credit gate; CREDITS_PER_SECOND below mirrors it
// for display only.

import { useCallback, useRef } from 'react'

import { useAuthContext } from '@/hooks/use-auth-context'
import { getApiBaseUrl } from '@/lib/api-config'
import {
  apiErrorCode,
  apiErrorNumber,
  apiErrorMessage,
} from '@/lib/api-error-body'
import { getBackendAuthToken } from '@/lib/backend-auth-token'
import {
  fetchPreviewEligibility,
  type PreviewEligibility,
} from '@/lib/free-preview'
import { isWorkspaceSelectionRequired } from '@/lib/tool-workspace-selection'

// Mirror of the server cost table (display only — the server re-checks).
/**
 * Last-resort fallback only — the live table comes from the server.
 *
 * This constant used to BE the price the page displayed, and it silently went
 * stale the moment the server was repriced: when /models/seedance-2-5 switched
 * from Seedance 2.0 Mini to the real 2.5 endpoint, the server went to 238/510
 * per second while this said 50/100, so the page advertised a 500-credit clip
 * and the server charged 2,550 for it. `loadCreditsPerSecond` below reads the
 * authoritative table instead; this is what renders for the one frame before
 * that resolves, and if the fetch fails.
 */
export const CREDITS_PER_SECOND: Record<string, number> = {
  '480p': 238,
  '720p': 510,
}

/** Fetch the server's own price table. Public (no auth) — see the tool-cost
 *  exemption in apps/api/middleware/auth.py. */
export async function loadCreditsPerSecond(): Promise<Record<
  string,
  number
> | null> {
  try {
    // web-arch-allow-direct-fetch: public price bootstrap, no auth context.
    const res = await fetch(`${getApiBaseUrl()}/api/tools/seedance-2-5/cost`)
    if (!res.ok) return null
    const body = (await res.json()) as {
      data?: { creditsPerSecond?: Record<string, number> }
    }
    const table = body.data?.creditsPerSecond
    return table && Object.keys(table).length > 0 ? table : null
  } catch {
    return null
  }
}

export function creditCostFrom(
  table: Record<string, number>,
  durationSeconds: number,
  resolution: string,
): number {
  const perSec = table[resolution] ?? table['720p'] ?? Object.values(table)[0]
  return Math.ceil(durationSeconds * perSec)
}

export function computeCreditCost(
  durationSeconds: number,
  resolution: string,
): number {
  return creditCostFrom(CREDITS_PER_SECOND, durationSeconds, resolution)
}

const WORKSPACE_KEY = 'seedance_2_5_workspace'

export interface Seedance25Workspace {
  orgId: string
  authOrgId: string
  orgSlug: string
  brandId: string
  brandSlug: string
}

interface StoredWorkspace {
  workspace: Seedance25Workspace
  userId: string | null
}

function readStoredWorkspace(): StoredWorkspace | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(WORKSPACE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as StoredWorkspace
    return stored?.workspace?.orgId ? stored : null
  } catch {
    return null
  }
}

export interface GenerateParams {
  workspaceId?: string
  prompt: string
  duration: number
  resolution: string
  aspectRatio: string
  generateAudio: boolean
  /** Reference media as public HTTPS or data URLs (fal caps: 9 / 3 / 3). */
  imageUrls?: string[]
  videoUrls?: string[]
  audioUrls?: string[]
  seed?: number
}

export type GenerateResult =
  | {
      status: 'ok'
      requestId: string
      creditsCharged: number
      balanceAfter: number
    }
  | { status: 'insufficient'; needed?: number; balance?: number }
  | { status: 'plan_required'; requiredPlan: 'creator' }
  | { status: 'workspace_selection_required' }
  | { status: 'unauthenticated' }
  | { status: 'error'; message: string }

export interface PollResult {
  failure?: ToolFailure | null
  status: string
  videoUrl: string | null
  queuePosition?: number | null
  /** True only after the server committed the exactly-once failure refund. */
  refunded?: boolean
  /** Set when status is 'REJECTED' — fal's own end-user explanation. */
  message?: string | null
  /** 'preview' when this clip was the org's one free watermarked run. */
  deliveryMode?: 'full' | 'preview' | null
}

export function useSeedance25() {
  const { getToken, user } = useAuthContext()
  const ensureInFlight = useRef<Promise<Seedance25Workspace> | null>(null)

  // Create (or reuse) and activate a self-auth workspace so credits resolve.
  // Idempotent server-side; the in-flight ref collapses concurrent callers.
  const ensureWorkspace =
    useCallback(async (): Promise<Seedance25Workspace> => {
      const userId = user?.id ?? null
      const cached = readStoredWorkspace()
      if (cached && userId && cached.userId === userId) return cached.workspace
      if (ensureInFlight.current) return ensureInFlight.current

      const run = (async () => {
        const baseUrl = getApiBaseUrl()
        const token = await getBackendAuthToken(getToken, { skipCache: true })
        // web-arch-allow-direct-fetch: existing imperative marketing tool bootstrap; migration is outside this entitlement review fix.
        const res = await fetch(`${baseUrl}/api/tools/seedance-2-5/workspace`, {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: 'My workspace' }),
        })
        if (!res.ok) throw new Error(`workspace_http_${res.status}`)
        const body = (await res.json()) as { data?: Seedance25Workspace }
        if (!body.data?.orgId) throw new Error('workspace_invalid_response')

        // Activate the org so org-scoped reads (credit balance) + the generate
        // call resolve against it; the JWT otherwise lags a just-created org.
        const { tokenManager } = await import('@/lib/self-auth/token-manager')
        tokenManager.setActiveOrg(body.data.authOrgId)
        await tokenManager.getToken({ forceRefresh: true })

        sessionStorage.setItem(
          WORKSPACE_KEY,
          JSON.stringify({
            workspace: body.data,
            userId,
          } satisfies StoredWorkspace),
        )
        return body.data
      })()

      ensureInFlight.current = run
      try {
        return await run
      } finally {
        ensureInFlight.current = null
      }
    }, [getToken, user])

  const generate = useCallback(
    async (params: GenerateParams): Promise<GenerateResult> => {
      const baseUrl = getApiBaseUrl()
      const token = await getBackendAuthToken(getToken, { skipCache: true })
      if (!token) return { status: 'unauthenticated' }
      let res: Response
      try {
        // web-arch-allow-direct-fetch: existing imperative generation request; migration is outside this entitlement review fix.
        res = await fetch(`${baseUrl}/api/tools/seedance-2-5/generate`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...(params.workspaceId ? { workspaceId: params.workspaceId } : {}),
            prompt: params.prompt,
            duration: params.duration,
            resolution: params.resolution,
            aspectRatio: params.aspectRatio,
            generateAudio: params.generateAudio,
            ...(params.imageUrls?.length
              ? { imageUrls: params.imageUrls }
              : {}),
            ...(params.videoUrls?.length
              ? { videoUrls: params.videoUrls }
              : {}),
            ...(params.audioUrls?.length
              ? { audioUrls: params.audioUrls }
              : {}),
            ...(params.seed !== undefined ? { seed: params.seed } : {}),
          }),
        })
      } catch {
        return {
          status: 'error',
          message: 'Could not connect. Check your connection and try again.',
        }
      }
      if (res.status === 402) {
        const body: unknown = await res.json().catch(() => null)
        if (apiErrorCode(body) === 'fal_video_plan_required') {
          return { status: 'plan_required', requiredPlan: 'creator' }
        }
        return {
          status: 'insufficient',
          needed: apiErrorNumber(body, 'needed'),
          balance: apiErrorNumber(body, 'balance'),
        }
      }
      if (res.status === 401) return { status: 'unauthenticated' }
      if (await isWorkspaceSelectionRequired(res)) {
        return { status: 'workspace_selection_required' }
      }
      if (!res.ok) {
        const errorBody: unknown = await res.json().catch(() => null)
        return {
          status: 'error',
          message:
            apiErrorMessage(errorBody) ??
            'Could not start generation. Please try again later.',
        }
      }
      const body = (await res.json()) as {
        data?: {
          requestId: string
          creditsCharged: number
          balanceAfter: number
        }
      }
      if (!body.data?.requestId)
        return {
          status: 'error',
          message: 'Could not start generation. Please try again later.',
        }
      return { status: 'ok', ...body.data }
    },
    [getToken],
  )

  const pollStatus = useCallback(
    async (requestId: string): Promise<PollResult> => {
      const baseUrl = getApiBaseUrl()
      const token = await getBackendAuthToken(getToken)
      // web-arch-allow-direct-fetch: existing imperative generation status poll; migration is outside this entitlement review fix.
      const res = await fetch(
        `${baseUrl}/api/tools/seedance-2-5/generate/${encodeURIComponent(requestId)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      )
      if (!res.ok) throw new Error(`status_http_${res.status}`)
      const body = (await res.json()) as { data?: PollResult }
      return body.data ?? { status: 'UNKNOWN', videoUrl: null }
    },
    [getToken],
  )

  /**
   * The free-watermarked-preview exposure. Called once per page by a signed-in
   * visitor whose plan refuses generation; the server deals the experiment arm
   * here and nowhere else. `null` = could not tell — the caller keeps whatever
   * the balance said.
   */
  const checkPreviewEligibility = useCallback(
    async (workspaceId: string | null): Promise<PreviewEligibility | null> => {
      const token = await getBackendAuthToken(getToken, { skipCache: true })
      return fetchPreviewEligibility(
        `${getApiBaseUrl()}/api/tools/seedance-2-5/preview-eligibility`,
        token,
        workspaceId,
      )
    },
    [getToken],
  )

  return { ensureWorkspace, generate, pollStatus, checkPreviewEligibility }
}

export interface HandoffResult {
  projectId: string
  orgSlug: string
  brandSlug: string
  path: string
}

/** Open a finished clip as a Creatives canvas. Returns null on any failure —
 *  the studio keeps the download path either way. */
export async function handoffToCanvas(
  token: string,
  requestId: string,
  prompt: string,
  brandId?: string,
): Promise<HandoffResult | null> {
  try {
    // web-arch-allow-direct-fetch: imperative landing-page handoff.
    const res = await fetch(
      `${getApiBaseUrl()}/api/tools/seedance-2-5/handoff`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          brandId ? { requestId, prompt, brandId } : { requestId, prompt },
        ),
      },
    )
    if (!res.ok) return null
    const body = (await res.json()) as { data?: HandoffResult }
    return body.data?.path ? body.data : null
  } catch {
    return null
  }
}

/**
 * This surface's permanent history reads the one shared endpoint wrapper, so a
 * field the endpoint gains cannot arrive on some tool pages and not others.
 */
export {
  historyDeliveryMode,
  loadStoredGenerations,
  type StoredGeneration,
} from '@/lib/tool-generations'
